import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';

import { TimePicker } from 'ui-components/components/time-picker';
import { Input } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text/index';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { noop } from 'ui-components/default-props/noop';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';

import { messages as v1Messages } from '../../modal-components/pickup-window/messages';
import { messages } from '../messages';
import style from './style.module.css';

// NOTE: TimePicker for whatever reason, _displays_ in h:mm A format
// but the underying value that determines "selection" is in h:mmA format
const getTimePickerFormattedTime = (time, timeZoneIana) => {
  if (!time || !timeZoneIana) {
    return '';
  }

  const warehouseDateTime = DateTime.fromFormat(time, 'HH:mm:ss', { zone: timeZoneIana });
  return warehouseDateTime.toFormat('h:mma');
};

const getTimePickerTimeInUTC = (time, timeZoneIana) => {
  const userDateTime = DateTime.fromFormat(time, 'h:mm a', { zone: timeZoneIana });
  return userDateTime.toUTC().toFormat('HH:mm:ss');
};

const getTimezoneAbbreviation = (timeZoneIana) => {
  const abbreviation = DateTime.now().setZone(timeZoneIana).offsetNameShort;
  return abbreviation ? ` ${abbreviation}` : '';
};

/**
 * PickupWindowTimes component for selecting pickup time window
 * @param {object} props
 * @param {boolean} [props.hasFixedTimeWindow] - Whether the time window is fixed
 * @param {(time: string) => void} [props.onStartTimeChange] - Callback when start time is changed
 * @param {(time: string) => void} [props.onEndTimeChange] - Callback when end time is changed
 * @param {string} [props.pickupWindowStartTime] - start time value in "HH:mm:ss" format
 * @param {string} [props.pickupWindowEndTime] - end time value in "HH:mm:ss" format
 * @param {string} [props.timeZoneIana] - IANA timezone that the times will represent (e.g., "America/Los_Angeles")
 * @returns {React.ReactElement} The PickupWindowTimes component
 */
export const PickupWindowTimes = ({
  hasFixedTimeWindow = false,
  onStartTimeChange = noop,
  onEndTimeChange = noop,
  pickupWindowStartTime = '',
  pickupWindowEndTime = '',
  timeZoneIana = '',
}) => {
  const startTime = getTimePickerFormattedTime(pickupWindowStartTime, timeZoneIana);
  const endTime = getTimePickerFormattedTime(pickupWindowEndTime, timeZoneIana);

  const handleOnStartTimeChanged = (time) => {
    const utcTime = getTimePickerTimeInUTC(time, timeZoneIana);
    onStartTimeChange(utcTime);
  };

  const handleOnEndTimeChanged = (time) => {
    const utcTime = getTimePickerTimeInUTC(time, timeZoneIana);
    onEndTimeChange(utcTime);
  };

  if (hasFixedTimeWindow) {
    const timezoneAbbr = getTimezoneAbbreviation(timeZoneIana);
    const displayValue = `${startTime} - ${endTime}${timezoneAbbr}`;
    return (
      <div>
        <div className={style['fixed-window-label']}>
          <Text textType={TextType.Label} value={<FormattedMessage {...v1Messages.pickupWindowFrom} />} />
          <Tooltip>
            <TooltipContent>
              <FormattedMessage {...messages.fixedPickupWindowTooltip} />
            </TooltipContent>
            <div className={style['info-icon']}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </div>
          </Tooltip>
        </div>
        <Input disabled value={displayValue} />
      </div>
    );
  }

  return (
    <div>
      <LabelWithRequiredIndicator>
        <Text textType={TextType.Label} value={<FormattedMessage {...v1Messages.pickupWindowFrom} />} />
      </LabelWithRequiredIndicator>
      <div className={style['pickup-window-times']}>
        <TimePicker id='start-time' onTimeSelected={handleOnStartTimeChanged} selectedValue={startTime} timeFormat='h:mm a' />
        <Text
          className={style['pickup-window-to-label']}
          textType={TextType.Paragraph}
          value={<FormattedMessage {...v1Messages.pickupWindowTo} />}
        />
        <TimePicker id='end-time' onTimeSelected={handleOnEndTimeChanged} selectedValue={endTime} timeFormat='h:mm a' />
      </div>
    </div>
  );
};
